/**
 * SettlementIndex — the sparse planet-wide carve index (W2b-1 Task 2).
 *
 * This is the rivers-`carveAt` pattern (see rivers.ts) adapted from river
 * polylines to settlement ROADS and PARCELS. Grown regions (CityGraphs) are
 * folded in one at a time; every non-TUNNEL / non-BRIDGE edge becomes a SEGMENT
 * feature (a road bed to flatten toward) and every parcel becomes a QUAD feature
 * (a platform at its frozen level). Features are smeared into a planet-wide
 * cube-face bucket grid so the terrain hot path (Task 3's `settlementCarve`
 * stage) can answer "what settlement surface is near this direction?" with ONE
 * bucket lookup and an early-out on the empty case.
 *
 * WHY A SEPARATE GRID FROM rivers' GRID_G
 * ---------------------------------------
 * Rivers use a fixed 96-cell face grid sized for ~117 m channel influence. Carve
 * features are an order of magnitude smaller (a road bed is metres wide, its
 * blend a few metres more), so the carve grid picks its OWN per-planet bucket
 * count `CARVE_G` such that one bucket is at least 2·CARVE_MAX_M wide. That keeps
 * every feature's influence (≤ CARVE_MAX_M) inside its own bucket plus the
 * immediate neighbours it is smeared into — so a query is a single-bucket scan.
 *
 * DETERMINISM
 * -----------
 *   - addRegion(g) is idempotent per region cell (keyed packCell(regionId)) and
 *     region-order-independent: feature identity is (packCell(regionId),
 *     localIndex) — edges first in edge-index order, then parcels in parcel-index
 *     order. carveAt breaks a k-tie by that composite key (NOT by bucket
 *     insertion order or global feature index), so adding regions A-then-B and
 *     B-then-A yield byte-identical query results.
 *   - All angular math routes through pmath (portable, bit-exact); distances use
 *     the exactly-specified Math.sqrt. No banned Math.* / ** — the pmath-ban gate
 *     covers this file.
 *
 * WORKER TOPOLOGY (W2b-2)
 * ----------------------
 * A SettlementIndex is PER-PROCESS: it holds live Maps/Sets and a reference to the
 * PlanetSampler, so it is neither structured-cloneable nor transferable and cannot
 * be posted across a worker boundary. W2b-2's region workers therefore do NOT ship
 * the index — they ship the CityGraph BUFFERS and each worker REBUILDS its own
 * index locally via `deserializeCityGraph` + `addRegion` per transferred buffer.
 * Because addRegion is idempotent and region-order-independent (proven by the
 * carve oracle), the fold order across workers does not matter — every process
 * converges on a byte-identical index. Rebuild cost is low single-digit ms per
 * region (it re-samples the node beds and re-smears the features), so a worker
 * standing up its index from a handful of buffers is cheap.
 *
 * DEVIATION FROM THE TASK-2 INTERFACE SKETCH (documented, deliberate)
 * ------------------------------------------------------------------
 * The brief sketched `constructor(radius: number)` and
 * `carveAt(dx,dy,dz,out)`. Two later, more-specific design points in the same
 * plan make that sketch insufficient, so this implementation adopts the superset
 * the plan actually requires:
 *   1. The constructor takes the PlanetSampler, not a bare radius. Re-sampling
 *      per-endpoint road-bed heights at addRegion time (the plan's binding
 *      requirement — heights are NOT stored in a CityGraph) needs `sample()`, and
 *      STAIR quantization (STAIR_RISE_M metres → amplitude units) needs
 *      `amplitude`. The sampler provides radius, amplitude AND sample() in one
 *      argument. (Imported type-only, as rivers.ts does, so there is no runtime
 *      import cycle with universe.ts.)
 *   2. carveAt takes `wildH` (the pre-carve height, amplitude units) as the plan
 *      amends for Task 3: the hit needs it to flag `retaining` and to ease the
 *      cut toward 0 on cliffs (see CARVE_CLAMP_M).
 */
import * as pmath from "../pmath.js";
import { graphStreetSurface } from './graphStreetSurface.js';
import { StreetSurfaceIndex } from './streetSurface.js';
import { packCell } from "./cells.js";
import {
  type CityGraph,
  type RegionFrame,
  dirToLocal,
  localToDir,
  EDGE_STAIR,
  EDGE_TUNNEL,
  EDGE_BRIDGE,
} from "./citygraph.js";
import type { PlanetSampler } from "../universe.js";

// ── knobs (defined here; Task 5 gathers them into CARVE_KNOBS, see bottom) ─────
//
// Road half-WIDTHS are W/2. terracePath is the TERRACE edge's tread width. tunnel
// and bridge are listed for completeness but never carve the surface (tunnels are
// under it — TUNNEL_NO_CARVE; bridges span water — both are excluded features).
export const ROAD_W = { arterial: 7, street: 5, stair: 3.2, tunnel: 4, terracePath: 3.2, bridge: 6 } as const;
/** Bed → wild ease band beyond a road half-width, metres. */
export const ROAD_BLEND_M = 6;
/** Platform → wild ease band beyond a parcel quad, metres. */
export const PARCEL_BLEND_M = 3;
/** Max feature half-extent + blend, metres — sizes the bucket grid. */
export const CARVE_MAX_M = 26;
/** Tunnels do not flatten the surface (they run beneath it). */
export const TUNNEL_NO_CARVE = true;
/** STAIR tread rise, metres — the visible stair-step quantum of a stair bed. */
export const STAIR_RISE_M = 0.45;
/**
 * A cut deeper than this (|level − wild h|, metres) is flagged `retaining`.
 * W2b-2.6: raised 2.5 → 4.5 so retaining-wall fragments are RARER (the user
 * wants them scarce ahead of the T2 city-wall generator).
 */
export const RETAIN_CUT_M = 4.5;
/** Beyond this cut depth (metres) the carve eases toward 0 — roads don't cut cliffs. */
export const CARVE_CLAMP_M = 14;

/** The carve hit — see field docs. Matches the Task-2 binding shape. */
export interface CarveHit {
  /** Independent union of road/junction coverage; legacy callers may omit it. */
  road?: number;
  /** Target surface level, amplitude units. */ level: number;
  /** Blend 0..1 toward level (1 = fully flattened). */ k: number;
  /** Feature kind: EDGE_* for roads, 100+use for parcels. */ kind: number;
  /** Perpendicular distance to the feature axis (m), for W2b-2 rendering. */ dist: number;
  /** Feature half-width (m; a road's v11 half-width, 0 for parcels) — with `dist`
   *  this yields the road-surface SDF the terrain PAINT reads (W2d-3 wrap). */
  half: number;
  /** True where the cut exceeds RETAIN_CUT_M against wild terrain. */ retaining: boolean;
}

/** Road half-width (metres) indexed by EDGE_* kind; tunnel/bridge slots unused. */
const HALF_W = [
  ROAD_W.arterial * 0.5, // EDGE_ARTERIAL
  ROAD_W.street * 0.5, //   EDGE_STREET
  ROAD_W.stair * 0.5, //    EDGE_STAIR
  ROAD_W.tunnel * 0.5, //   EDGE_TUNNEL   (excluded)
  ROAD_W.terracePath * 0.5, // EDGE_TERRACE
  ROAD_W.bridge * 0.5, //   EDGE_BRIDGE   (excluded)
];

/** Hermite smoothstep on [0,1] (clamped) — the ease shared by every blend band. */
function smooth01(x: number): number {
  const t = x < 0 ? 0 : x > 1 ? 1 : x;
  return t * t * (3 - 2 * t);
}

/**
 * Cube-face bucket id of a unit direction at grid resolution G (per face axis).
 * A verbatim port of rivers' `cellOf` but parameterised by G — same "same dir →
 * same cell, nearby dirs → nearby cells" contract; seams handled by smearing.
 */
function cellOfG(x: number, y: number, z: number, G: number): number {
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  const az = Math.abs(z);
  let face: number;
  let u: number;
  let v: number;
  if (ax >= ay && ax >= az) {
    const s = 1 / ax;
    face = x > 0 ? 0 : 1;
    u = (x > 0 ? -z : z) * s;
    v = y * s;
  } else if (ay >= az) {
    const s = 1 / ay;
    face = y > 0 ? 2 : 3;
    u = x * s;
    v = (y > 0 ? -z : z) * s;
  } else {
    const s = 1 / az;
    face = z > 0 ? 4 : 5;
    u = (z > 0 ? x : -x) * s;
    v = y * s;
  }
  const cu = Math.min(G - 1, Math.max(0, ((u * 0.5 + 0.5) * G) | 0));
  const cv = Math.min(G - 1, Math.max(0, ((v * 0.5 + 0.5) * G) | 0));
  return (face * G + cv) * G + cu;
}

// A small scratch object the query fills for the current candidate feature; JS
// is single-threaded so a shared module scratch is safe and allocation-free.
const _cand = { k: 0, level: 0, dist: 0, kind: 0, half: 0 };

/**
 * The sparse settlement carve index. Fold materialized regions in with
 * {@link addRegion}; query with {@link carveAt}.
 */
export class SettlementIndex {
  private readonly sampler: PlanetSampler;
  private readonly R: number;
  private readonly amplitude: number;
  /** Bucket count per cube-face axis (see CARVE_G doc below). */
  private readonly G: number;
  /** One bucket's arc width, metres — the smear margin unit. */
  private readonly cellWidthM: number;

  /** packCell(regionId) of every folded region — the idempotence guard. */
  private readonly added = new Set<number>();
  private readonly streetSurfaces = new Map<number, StreetSurfaceIndex>();
  /** bucket id → feature indices overlapping it (+ blend margin). */
  private readonly grid = new Map<number, number[]>();

  // ── feature columns (parallel; index = global feature id) ─────────────────
  private readonly fFrame: RegionFrame[] = [];
  private readonly fKind: number[] = []; //     EDGE_* (road) | 100+use (parcel)
  private readonly fRegionKey: number[] = []; // packCell(regionId) — tie-break primary
  private readonly fLocalIdx: number[] = []; //  per-region emit order — tie-break secondary
  private readonly fIsParcel: boolean[] = [];
  private readonly fIsStair: boolean[] = [];
  private readonly fHalf: number[] = []; //     road half-width (m); parcel: 0
  // segment endpoints (region-local metres) + re-sampled bed levels (amp units)
  private readonly fAu: number[] = [];
  private readonly fAv: number[] = [];
  private readonly fBu: number[] = [];
  private readonly fBv: number[] = [];
  private readonly fALevel: number[] = [];
  private readonly fBLevel: number[] = [];
  // parcel geometry (8 floats, region-local CCW) + frozen platform level
  private readonly fPoly: Float64Array[] = [];
  private readonly fLevel: number[] = [];

  // reused build/query scratch
  private readonly _dir: number[] = [0, 0, 0];
  private readonly _q: [number, number] = [0, 0];

  constructor(sampler: PlanetSampler) {
    this.sampler = sampler;
    this.R = sampler.def.radius;
    this.amplitude = sampler.amplitude;
    // CARVE_G: the largest bucket count whose bucket width (R·π/2)/G is still
    // ≥ 2·CARVE_MAX_M, so a feature's ≤CARVE_MAX_M influence never spans more
    // than its own bucket and the neighbours it is smeared into. On a body too
    // small to hold even one such bucket (R·π/2 < 2·CARVE_MAX_M — sub-km moons)
    // it clamps to 1 (the whole face is one bucket) — the smallest possible.
    const faceArc = (this.R * Math.PI) / 2;
    this.G = Math.max(1, Math.floor(faceArc / (2 * CARVE_MAX_M)));
    this.cellWidthM = faceArc / this.G;
  }

  /** True once the region cell (packCell key) has been folded in. */
  has(cellKey: number): boolean {
    return this.added.has(cellKey);
  }

  /**
   * Fold one materialized region's graph into the index. Idempotent per region
   * cell: a second call with the same regionId is a no-op, so RegionService may
   * re-materialize freely. Emits edge features (non-TUNNEL, non-BRIDGE) then
   * parcel features, assigning each a per-region localIndex in that fixed order.
   */
  addRegion(g: CityGraph): void {
    const key = packCell(g.regionId);
    if (this.added.has(key)) return;
    this.added.add(key);
    this.streetSurfaces.set(key,new StreetSurfaceIndex(graphStreetSurface(g,e=>g.edgeWidth[e]>0?g.edgeWidth[e]:HALF_W[g.edgeKind[e]]*2)));

    const frame = g.frame;
    const nodeCount = g.nodeU.length;
    // Re-sample each endpoint's wild road-bed height ONCE, cached by node index.
    // climate off and settlement carving skipped to avoid recursively reading
    // this index; heights are amplitude units, matching parcel.level.
    const nodeLevel = new Float64Array(nodeCount);
    const sampled = new Uint8Array(nodeCount);
    const levelOf = (ni: number): number => {
      if (sampled[ni] === 0) {
        localToDir(frame, g.nodeU[ni], g.nodeV[ni], this._dir);
        nodeLevel[ni] = this.sampler.sample(this._dir[0], this._dir[1], this._dir[2], SAMP, false, false, true).h;
        sampled[ni] = 1;
      }
      return nodeLevel[ni];
    };

    let local = 0;
    // Edges → segment features (skip tunnels/bridges).
    for (let e = 0; e < g.edgeKind.length; e++) {
      const kind = g.edgeKind[e];
      if (kind === EDGE_TUNNEL || kind === EDGE_BRIDGE) continue;
      const a = g.edgeA[e];
      const b = g.edgeB[e];
      const fi = this.fKind.length;
      this.fFrame.push(frame);
      this.fKind.push(kind);
      this.fRegionKey.push(key);
      this.fLocalIdx.push(local++);
      this.fIsParcel.push(false);
      this.fIsStair.push(kind === EDGE_STAIR);
      // v11: carve at the edge's RESOLVED per-edge width (spec: "carve ROAD_W
      // becomes per-edge"); the kind-keyed HALF_W stays as the fallback for
      // graphs built outside the classify pipeline (unit-test builders).
      this.fHalf.push(g.edgeWidth[e] > 0 ? g.edgeWidth[e] * 0.5 : HALF_W[kind]);
      this.fAu.push(g.nodeU[a]);
      this.fAv.push(g.nodeV[a]);
      this.fBu.push(g.nodeU[b]);
      this.fBv.push(g.nodeV[b]);
      this.fALevel.push(levelOf(a));
      this.fBLevel.push(levelOf(b));
      // parcel-only columns keep alignment
      this.fPoly.push(EMPTY_POLY);
      this.fLevel.push(0);
      this.smearSegment(fi);
    }

    // Parcels → quad features at their frozen level.
    for (let p = 0; p < g.parcels.length; p++) {
      const parcel = g.parcels[p];
      if (parcel.poly.length < 8) continue; // defensive: only quad parcels carve
      const fi = this.fKind.length;
      this.fFrame.push(frame);
      this.fKind.push(100 + parcel.use);
      this.fRegionKey.push(key);
      this.fLocalIdx.push(local++);
      this.fIsParcel.push(true);
      this.fIsStair.push(false);
      this.fHalf.push(0);
      this.fAu.push(0);
      this.fAv.push(0);
      this.fBu.push(0);
      this.fBv.push(0);
      this.fALevel.push(0);
      this.fBLevel.push(0);
      this.fPoly.push(parcel.poly);
      this.fLevel.push(parcel.level);
      this.smearParcel(fi);
    }
  }

  // ── smear: insert a feature into every bucket its influence can reach ──────
  //
  // Mirrors rivers' `smear`: stamp discs of radius (influence + one cell) along
  // the feature, ring-probing tangent offsets so every overlapping bucket (seams
  // included — cellOfG is scale-invariant) receives the feature. Over-coverage is
  // harmless (a few extra bucket entries); UNDER-coverage would make a single
  // bucket lookup miss a feature — which the brute-vs-bucket oracle would catch.

  /** Add feature fi to the bucket of region-local point (u,v), deduped via `seen`. */
  private stampPoint(fi: number, frame: RegionFrame, u: number, v: number, seen: Set<number>): void {
    localToDir(frame, u, v, this._dir);
    const cell = cellOfG(this._dir[0], this._dir[1], this._dir[2], this.G);
    if (seen.has(cell)) return;
    seen.add(cell);
    let bucket = this.grid.get(cell);
    if (bucket === undefined) this.grid.set(cell, (bucket = []));
    bucket.push(fi);
  }

  /** Stamp a disc of radius `rM` around region-local (u,v): centre + two rings. */
  private stampDisc(fi: number, frame: RegionFrame, u: number, v: number, rM: number, seen: Set<number>): void {
    this.stampPoint(fi, frame, u, v, seen);
    for (let ring = 0; ring < 2; ring++) {
      const rr = rM * (ring === 0 ? 0.6 : 1.0);
      for (let s = 0; s < 8; s++) {
        const ang = (s / 8) * pmath.TWO_PI;
        this.stampPoint(fi, frame, u + pmath.cos(ang) * rr, v + pmath.sin(ang) * rr, seen);
      }
    }
  }

  private smearSegment(fi: number): void {
    const frame = this.fFrame[fi];
    const au = this.fAu[fi];
    const av = this.fAv[fi];
    const bu = this.fBu[fi];
    const bv = this.fBv[fi];
    const influence = this.fHalf[fi] + ROAD_BLEND_M;
    const discR = influence + this.cellWidthM;
    const len = Math.sqrt((bu - au) * (bu - au) + (bv - av) * (bv - av));
    // Walk the axis at ≤ half a bucket so consecutive discs overlap laterally.
    const steps = Math.max(1, Math.ceil(len / (this.cellWidthM * 0.5)));
    const seen = new Set<number>();
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      this.stampDisc(fi, frame, au + (bu - au) * t, av + (bv - av) * t, discR, seen);
    }
  }

  private smearParcel(fi: number): void {
    const frame = this.fFrame[fi];
    const poly = this.fPoly[fi];
    const nc = poly.length / 2; // W2d-3: block lots are n-gons, not just quads
    let cu = 0;
    let cv = 0;
    for (let c = 0; c < nc; c++) {
      cu += poly[2 * c];
      cv += poly[2 * c + 1];
    }
    cu /= nc;
    cv /= nc;
    const seen = new Set<number>();
    // Centroid disc large enough to reach every corner + blend, plus a disc at
    // each corner (parcels are tiny relative to a bucket, so this over-covers).
    let maxCorner = 0;
    for (let c = 0; c < nc; c++) {
      const d = Math.sqrt(
        (poly[2 * c] - cu) * (poly[2 * c] - cu) + (poly[2 * c + 1] - cv) * (poly[2 * c + 1] - cv),
      );
      if (d > maxCorner) maxCorner = d;
    }
    // Single-bucket invariant guard: a feature's influence must stay ≤ CARVE_MAX_M
    // so the smear (own bucket + neighbours) covers every bucket a lookup can hit.
    // A future knob change (bigger parcels / blend) could silently break this —
    // missed carves are terrain pops with no test failure — so fail loud here.
    const influenceR = maxCorner + PARCEL_BLEND_M;
    if (influenceR > CARVE_MAX_M) {
      throw new Error(
        `SettlementIndex: parcel influence radius ${influenceR.toFixed(2)}m exceeds ` +
          `CARVE_MAX_M ${CARVE_MAX_M}m (region ${this.fRegionKey[fi]}, parcel localIndex ` +
          `${this.fLocalIdx[fi]}) — bucket smear would under-cover, breaking the single-bucket invariant`,
      );
    }
    this.stampDisc(fi, frame, cu, cv, maxCorner + PARCEL_BLEND_M + this.cellWidthM, seen);
    for (let c = 0; c < nc; c++) {
      this.stampDisc(fi, frame, poly[2 * c], poly[2 * c + 1], PARCEL_BLEND_M + this.cellWidthM, seen);
    }
  }

  // ── query ─────────────────────────────────────────────────────────────────

  /**
   * Rivers-carveAt-shaped: ONE bucket lookup at (dx,dy,dz), scan its features,
   * pick the strongest carve. Returns false when nothing settled is near. On a
   * hit `out` is filled: `level`/`k` drive the height blend, `kind`/`dist` feed
   * the W2b-2 renderer, `retaining` flags a cut deeper than RETAIN_CUT_M against
   * `wildH` (the pre-carve height, amplitude units).
   *
   * Winner = strongest base k; ties break by (regionKey, localIndex) — NOT bucket
   * order — so the result is region-order-independent. The chosen feature's k is
   * then eased toward 0 if the cut exceeds CARVE_CLAMP_M (roads don't cut cliffs).
   *
   * NOTE: a fully cliff-clamped hit (cut ≥ 2·CARVE_CLAMP_M) returns TRUE with k=0
   * and retaining=true — the feature is present but carves nothing here. The
   * renderer contract for that k=0/retaining case is to be settled in W2b-2.
   */
  carveAt(dx: number, dy: number, dz: number, wildH: number, out: CarveHit): boolean {
    const bucket = this.grid.get(cellOfG(dx, dy, dz, this.G));
    if (bucket === undefined) return false;
    return this.selectAndFill(bucket, dx, dy, dz, wildH, out);
  }

  /**
   * Reference/oracle path: identical selection over ALL features (no bucket).
   * Tests cross-check {@link carveAt} against this to prove the smear inserts
   * every feature a single-bucket lookup must see. Not for the hot path.
   */
  carveAtBrute(dx: number, dy: number, dz: number, wildH: number, out: CarveHit): boolean {
    return this.selectAndFill(ALL_INDICES(this.fKind.length), dx, dy, dz, wildH, out);
  }

  private selectAndFill(
    feats: number[] | Iterable<number>,
    dx: number,
    dy: number,
    dz: number,
    wildH: number,
    out: CarveHit,
  ): boolean {
    let bestK = 0;
    let bestLevel = 0;
    let bestDist = 0;
    let bestKind = 0;
    let bestHalf = 0;
    let bestRK = 0;
    let bestLI = 0;
    let has = false;
    let road=8,lastRegion=-1;
    for (const fi of feats) {
      const region=this.fRegionKey[fi];
      if(region!==lastRegion){
        dirToLocal(this.fFrame[fi],dx,dy,dz,this._q);
        road=Math.min(road,this.streetSurfaces.get(region)!.sample(this._q[0],this._q[1]));
        lastRegion=region;
      }
      if (!this.evalFeature(fi, dx, dy, dz)) continue;
      const k = _cand.k;
      const rk = this.fRegionKey[fi];
      const li = this.fLocalIdx[fi];
      const better =
        !has ||
        k > bestK ||
        (k === bestK && (rk < bestRK || (rk === bestRK && li < bestLI)));
      if (better) {
        has = true;
        bestK = k;
        bestLevel = _cand.level;
        bestDist = _cand.dist;
        bestKind = _cand.kind;
        bestHalf = _cand.half;
        bestRK = rk;
        bestLI = li;
      }
    }
    if (!has) return false;

    // retaining + cliff clamp, both in METRES against the pre-carve wild height.
    const deltaM = Math.abs(bestLevel - wildH) * this.amplitude;
    let k = bestK;
    if (deltaM > CARVE_CLAMP_M) {
      // Ease k from full at CARVE_CLAMP_M to 0 at 2·CARVE_CLAMP_M — a road never
      // carves a cliff; that terrain is what stairs/tunnels/retaining walls are for.
      k *= 1 - smooth01((deltaM - CARVE_CLAMP_M) / CARVE_CLAMP_M);
    }
    out.level = bestLevel;
    out.k = k;
    out.kind = bestKind;
    out.dist = bestDist;
    out.half = bestHalf;
    out.retaining = deltaM > RETAIN_CUT_M;
    out.road = road;
    return true;
  }

  /**
   * Evaluate feature fi at unit direction (dx,dy,dz); writes k/level/dist/kind to
   * the module scratch `_cand` and returns true when k > 0 (the feature reaches
   * the query). Roads: flat at k=1 within the half-width, easing over ROAD_BLEND_M;
   * STAIR beds quantize the interpolated level to STAIR_RISE_M steps. Parcels: k=1
   * inside the quad, easing over PARCEL_BLEND_M outside.
   */
  private evalFeature(fi: number, dx: number, dy: number, dz: number): boolean {
    const frame = this.fFrame[fi];
    dirToLocal(frame, dx, dy, dz, this._q);
    const qu = this._q[0];
    const qv = this._q[1];

    if (!this.fIsParcel[fi]) {
      const au = this.fAu[fi];
      const av = this.fAv[fi];
      const du = this.fBu[fi] - au;
      const dv = this.fBv[fi] - av;
      const l2 = du * du + dv * dv;
      let t = l2 > 0 ? ((qu - au) * du + (qv - av) * dv) / l2 : 0;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const cuu = au + du * t;
      const cvv = av + dv * t;
      const dist = Math.sqrt((qu - cuu) * (qu - cuu) + (qv - cvv) * (qv - cvv));
      const half = this.fHalf[fi];
      let k: number;
      if (dist <= half) k = 1;
      else if (dist <= half + ROAD_BLEND_M) k = 1 - smooth01((dist - half) / ROAD_BLEND_M);
      else return false;
      let level = this.fALevel[fi] + (this.fBLevel[fi] - this.fALevel[fi]) * t;
      if (this.fIsStair[fi]) {
        const step = STAIR_RISE_M / this.amplitude; // metres → amplitude units
        level = Math.round(level / step) * step;
      }
      _cand.k = k;
      _cand.level = level;
      _cand.dist = dist;
      _cand.kind = this.fKind[fi];
      _cand.half = half;
      return true;
    }

    // Parcel: point-in-quad (CCW), else ease over the edge-distance blend band.
    const poly = this.fPoly[fi];
    const inside = pointInQuad(poly, qu, qv);
    let dist: number;
    let k: number;
    if (inside) {
      dist = 0;
      k = 1;
    } else {
      dist = quadEdgeDist(poly, qu, qv);
      if (dist > PARCEL_BLEND_M) return false;
      k = 1 - smooth01(dist / PARCEL_BLEND_M);
    }
    _cand.k = k;
    _cand.level = this.fLevel[fi];
    _cand.dist = dist;
    _cand.kind = this.fKind[fi];
    _cand.half = 0;
    return true;
  }
}

// ── shared build constants / small geometry helpers ──────────────────────────

/** A reused terrain-sample scratch for endpoint re-sampling (single-threaded). */
const SAMP = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
/** Placeholder poly for segment features (keeps the parallel columns aligned). */
const EMPTY_POLY = new Float64Array(0);

/** [0,1,...,n-1] — the brute-force feature order (ascending index). */
function ALL_INDICES(n: number): number[] {
  const a: number[] = new Array(n);
  for (let i = 0; i < n; i++) a[i] = i;
  return a;
}

/**
 * Point-in-polygon via crossing number — valid for the CONCAVE n-gon lots the
 * W2d-3 block subdivision emits (the old convex-orientation loop was quad-only).
 */
function pointInQuad(poly: Float64Array, px: number, py: number): boolean {
  const n = poly.length / 2;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[2 * i], yi = poly[2 * i + 1];
    const xj = poly[2 * j], yj = poly[2 * j + 1];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

// ── carve knobs (spec §12 debug-panel surface) ───────────────────────────────
//
// The tunable CARVE constants gathered into one frozen object, the carve-side
// sibling of growth.ts's GROWTH_KNOBS. Kept HERE (not folded into GROWTH_KNOBS)
// because every name below is defined in this file: a sibling export co-locates
// each knob with its definition and its use, and avoids growth.ts importing the
// carve module purely to re-export it. This is a pure re-export of the binding
// constants above — reading it is exactly reading each constant; it introduces
// NO behavior and NO new tuning point. Names map 1:1 to the constants (no
// aliasing). W2b's debug panel enumerates GROWTH_KNOBS and CARVE_KNOBS together.
export const CARVE_KNOBS = {
  ROAD_W,
  ROAD_BLEND_M,
  PARCEL_BLEND_M,
  CARVE_MAX_M,
  STAIR_RISE_M,
  RETAIN_CUT_M,
  CARVE_CLAMP_M,
  TUNNEL_NO_CARVE,
} as const;

/** Distance from (px,py) to the nearest boundary edge of an n-gon parcel. */
function quadEdgeDist(poly: Float64Array, px: number, py: number): number {
  let best = Infinity;
  const n = poly.length / 2;
  for (let k = 0; k < n; k++) {
    const k2 = (k + 1) % n;
    const ax = poly[2 * k];
    const ay = poly[2 * k + 1];
    const bx = poly[2 * k2];
    const by = poly[2 * k2 + 1];
    const dx = bx - ax;
    const dy = by - ay;
    const l2 = dx * dx + dy * dy;
    let t = l2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const qx = ax + dx * t;
    const qy = ay + dy * t;
    const d = Math.sqrt((px - qx) * (px - qx) + (py - qy) * (py - qy));
    if (d < best) best = d;
  }
  return best;
}
